import React, { useEffect, useRef, useState } from "react";
import { Search, Loader2, FileText, Bot, Network } from "lucide-react";
import { searchAllDossiers } from "../../lib/api";

const TYPE_META = {
  dossier:     { Icon: FileText, color: "#ff3b30", label: "DOSSIER" },
  ai_dossier:  { Icon: Bot,      color: "#a78bfa", label: "AI" },
  connection:  { Icon: Network,  color: "#60a5fa", label: "CONNECTION" },
};

/**
 * DossierPicker — small inline autocomplete: type, see ALL dossier-like
 * content (curated dossiers + AI-compiled deep dives + Connect-the-Dots
 * connections). Click one → calls onPick with
 * { title, slug, credibility, type, url }.
 * Lets the user drop ANY archive entity onto the Ties board as a pre-filled
 * node — every search must always return all three sources.
 */
export default function DossierPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      searchAllDossiers(q.trim(), 8)
        .then((items) => setResults(items))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <div className="relative" data-testid="dossier-picker">
      <div className="flex items-center gap-2">
        <Search size={11} className="text-zinc-500" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Drop a dossier, AI dive, or connection…"
          data-testid="dossier-picker-input"
          className="bg-[#050505] border border-white/15 focus:border-[#ffd60a] outline-none px-3 py-1.5 font-mono text-xs text-white w-72"
        />
        {loading && <Loader2 size={11} className="animate-spin text-zinc-500" />}
      </div>
      {open && results.length > 0 && (
        <ul className="absolute z-40 mt-1 w-80 border border-[#ffd60a]/40 bg-[#0a0a0a] max-h-72 overflow-y-auto"
            data-testid="dossier-picker-results">
          {results.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.dossier;
            const Icon = meta.Icon;
            return (
              <li key={`${r.type}-${r.slug}`}>
                <button
                  type="button"
                  data-testid={`dossier-pick-${r.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    onPick?.({
                      title: r.title,
                      slug: r.slug,
                      credibility: r.credibility,
                      type: r.type,
                      url: r.url,
                    });
                    setQ(""); setResults([]); setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-[#ffd60a]/10 border-b border-white/5 flex items-start gap-2"
                >
                  <Icon size={11} className="mt-0.5 shrink-0" style={{ color: meta.color }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-[13px] truncate">{r.title}</div>
                    <div className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase truncate">
                      <span style={{ color: meta.color }}>{meta.label}</span>
                      {r.subtitle ? ` · ${r.subtitle}` : ""}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
